import { LABEL_DEFINITIONS, defaultVisibilityFor } from './labelDefinitions';

// Resolve final per-label visibility + formatting across the three preset
// scopes. JS port of the spec's resolveLabelFormatting — same precedence and
// the same "ignore keys not in the schema" + sparse-patch semantics.
//
// @typedef {{ visible?: boolean, bold?: boolean, italic?: boolean }} LabelOverride
// @typedef {{ scope: 'system'|'group'|'user', scopeRef: string|null,
//             name: string, overrides: Object.<string, LabelOverride> }} LabelDisplayPreset
// @typedef {Object.<string, { visible: boolean, bold: boolean, italic: boolean }>} ResolvedLabelFormatting

// The non-sparse base: every label gets a definite visible/bold/italic from the
// capability module (visibility from Constants.defaultLabelFilters, formatting
// from each label's defaultFormat). This is the `schemaDefaults` layer.
/** @returns {ResolvedLabelFormatting} */
export function buildSchemaDefaults() {
  const out = {};
  for (const def of LABEL_DEFINITIONS) {
    const df = def.defaultFormat || {};
    out[def.key] = {
      visible: !!defaultVisibilityFor(def.key),
      bold: !!df.bold,
      italic: !!df.italic
    };
  }
  return out;
}

// Dependency-free deep clone of the flat resolved shape (avoids relying on
// structuredClone in older test/runtime contexts).
function cloneResolved(resolved) {
  const out = {};
  for (const key of Object.keys(resolved)) {
    out[key] = { ...resolved[key] };
  }
  return out;
}

/**
 * Precedence: user > group > system > schema default. Each preset layer is a
 * sparse patch; an unspecified field falls through to the next layer. Keys not
 * present in the schema base are ignored (handles renamed/removed labels).
 *
 * @param {ResolvedLabelFormatting} schemaDefaults
 * @param {LabelDisplayPreset|null} systemPreset
 * @param {LabelDisplayPreset|null} groupPreset
 * @param {LabelDisplayPreset|null} userPreset
 * @returns {ResolvedLabelFormatting}
 */
export function resolveLabelFormatting(
  schemaDefaults,
  systemPreset,
  groupPreset,
  userPreset
) {
  const result = cloneResolved(schemaDefaults);
  for (const preset of [systemPreset, groupPreset, userPreset]) {
    if (!preset || !preset.overrides) continue;
    for (const [key, ov] of Object.entries(preset.overrides)) {
      if (!result[key]) continue; // ignore labels not in the schema
      if (!ov) continue;
      if (ov.visible !== undefined) result[key].visible = ov.visible;
      if (ov.bold !== undefined) result[key].bold = ov.bold;
      if (ov.italic !== undefined) result[key].italic = ov.italic;
    }
  }
  return result;
}

/**
 * Fold a ResolvedLabelFormatting into the live `labelsSet` working-state shape:
 * flat visibility booleans (the existing representation) plus a sparse
 * `formatting` map the renderer reads. Non-label keys on baseLabelsSet
 * (diseasesMap, displayLabelMode, …) are preserved untouched. The formatting
 * map stays sparse — a label with no bold/italic emits no entry, so the result
 * is inert/byte-identical when nothing is formatted.
 *
 * @param {ResolvedLabelFormatting} resolved
 * @param {Object} [baseLabelsSet]
 * @returns {Object} a new labelsSet
 */
export function foldResolvedIntoLabelsSet(resolved, baseLabelsSet = {}) {
  const next = { ...baseLabelsSet };
  const formatting = { ...(baseLabelsSet.formatting || {}) };
  for (const [key, r] of Object.entries(resolved)) {
    next[key] = r.visible;
    if (r.bold || r.italic) {
      formatting[key] = { bold: !!r.bold, italic: !!r.italic };
    } else {
      delete formatting[key];
    }
  }
  next.formatting = formatting;
  return next;
}

// Convenience: resolve straight from the three presets to a labelsSet patch,
// using the capability-module schema defaults as the base layer.
export function resolveToLabelsSet(
  { systemPreset, groupPreset, userPreset },
  baseLabelsSet = {}
) {
  const resolved = resolveLabelFormatting(
    buildSchemaDefaults(),
    systemPreset,
    groupPreset,
    userPreset
  );
  return foldResolvedIntoLabelsSet(resolved, baseLabelsSet);
}
